import type {
  V2ShipmentDetail,
  V2ShipmentDevice,
  V2ShipmentStop,
} from '@gpx-spa/queries/src/getAccountShipmentV2';
import { namedTrackersFromShipment } from '@/utils/named-tracker-set';

export type ShipmentOriginDestinationView = {
  originAddress: string;
  destinationAddress: string;
  plannedPickup: string | null;
  actualPickup: string | null;
  plannedDelivery: string | null;
  actualDelivery: string | null;
};

function sortedStops(stops: V2ShipmentStop[] | undefined): V2ShipmentStop[] {
  if (!stops?.length) return [];
  return [...stops].sort((a, b) => a.sequence - b.sequence);
}

function stopLabel(stop: V2ShipmentStop | undefined): string {
  if (!stop) return '—';
  const name = stop.name?.trim();
  if (name) return name;
  return '—';
}

export function originDestinationFromShipment(
  shipment: V2ShipmentDetail | null | undefined,
): ShipmentOriginDestinationView {
  const stops = sortedStops(shipment?.stops);
  const origin = stops[0];
  const destination = stops.length > 1 ? stops[stops.length - 1] : stops[0];

  return {
    originAddress: stopLabel(origin),
    destinationAddress: stopLabel(destination),
    plannedPickup:
      origin?.planned_departure_at ?? origin?.planned_arrival_at ?? null,
    actualPickup: shipment?.started_at ?? null,
    plannedDelivery:
      destination?.planned_arrival_at ??
      destination?.planned_departure_at ??
      shipment?.end_at ??
      null,
    actualDelivery: shipment?.ended_at ?? null,
  };
}

/**
 * All devices attached to a shipment (ENG-2580 convoy).
 * Order follows `device_ids` (same as {@link namedTrackersFromShipment});
 * embedded `devices[]` fills name/serial/imei via merge.
 */
export function allShipmentDevices(
  shipment: V2ShipmentDetail | null | undefined,
): V2ShipmentDevice[] {
  if (!shipment) return [];
  const named = namedTrackersFromShipment(shipment);
  const embeddedById = new Map(
    (shipment.devices ?? [])
      .filter((d) => d?.id && d.id > 0)
      .map((d) => [d.id, d]),
  );
  return named.map((tracker) => {
    const embedded = embeddedById.get(tracker.id);
    return {
      ...(embedded ?? { id: tracker.id }),
      id: tracker.id,
      name: tracker.name ?? embedded?.name ?? null,
      serial: tracker.serial ?? embedded?.serial ?? null,
      imei: tracker.imei ?? embedded?.imei ?? null,
    };
  });
}

export function allDeviceIds(
  shipment: V2ShipmentDetail | null | undefined,
): number[] {
  return allShipmentDevices(shipment).map((device) => device.id);
}

export function shipmentCreatedAt(
  shipment: V2ShipmentDetail | null | undefined,
): string | null {
  return shipment?.created_at ?? null;
}
